import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';

function DeleteAccount() {

  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: deleteReasons,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['deleteAccountReasons'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/delete-account-reason-api`, {
        withCredentials: true
      });
      return res.data;
    },
    staleTime: 0,         // fetch every time
    cacheTime: Infinity   // never garbage collect cache
  });


const [selectedReason, setSelectedReason] = useState(null);
const [reasonSuggestion, setReasonSuggestion] = useState('');
const [password, setPassword] = useState('');

  const handleCheckboxChange = (e) => {
    setSelectedReason(e.target.value); // for simplicity, allow only one reason
  };

  
  const handleDeleteAccount = async () => {
    if (!selectedReason && reasonSuggestion.trim() === "") {
      toast.error("Select a reason or enter a suggestion.");
      return;
    }
  
    if (!password) {
      toast.error("Please confirm your password.");
      return;
    }
  
    try {
      const res = await axios.put(
        `${API_URL}/delete-my-account-api`,
        {
          reason: selectedReason,
          reason_suggestion: reasonSuggestion,
          password
        },
        { withCredentials: true }
      );
  
      toast.success("Account deleted successfully.");

      queryClient.invalidateQueries(['deleteAccountReasons']);
         
        navigate('/login');
  
    } catch (err) {
      toast.error(err.response?.data?.error || "Account deletion failed.");
    }
  };
  

  return (
    <div className='scrollPageContainer theme-light'>
      <div className='main-wrapper'>

        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer />

        <div className='page-wrapper'>
          <div className='content container-fluid'>

                            <div className='delete-header-custom'>
                              
                              <h1>Delete Your Account</h1>
                              <p>This action cannot be undone. Please read carefully.</p>
                            </div>
            
                            <div className='delete-body-custom'>
                              
                              <div className='danger-alert'>
                                <div className='danger-alert-title'>
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  Remember once you delete:
                                </div>
                                <ul className='danger-list'>
                                  <li>All your personal data (name, email, phone number, passwords) will be permanently deleted</li>
                                  <li>You won't be able to login into the system ever again</li>
                                </ul>
                              </div>
            
                              <div className='section-title'>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Please tell us why you're leaving
                              </div>
            
                              {isLoading ? (
                                <div className='loading-state'>
                                  <div className='spinner-custom'></div>
                                  <div className='loading-text'>Loading reasons...</div>
                                </div>
                              ) : isError ? (
                                <div className='alert alert-danger' role='alert'>
                                  <strong>Error!</strong> Failed to load reasons. Please refresh the page.
                                </div>
                              ) : (
                                <div className='reasons-list'>
                                  {deleteReasons && deleteReasons.map((reason) => (
                                    <div 
                                      key={reason.id} 
                                      className={`reason-checkbox ${selectedReason === reason.id.toString() ? 'selected' : ''}`}
                                      onClick={() => setSelectedReason(reason.id.toString())}
                                    >
                                      <label>
                                        <input
                                          type="checkbox"
                                          name="reason"
                                          id={`id_${reason.id}`}
                                          value={reason.id}
                                          checked={selectedReason === reason.id.toString()}
                                          onChange={handleCheckboxChange}
                                        />
                                        {reason.reason}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
            
                              <div style={{ marginTop: '25px' }}>
                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '10px', display: 'block' }}>
                                  Any other reason or suggestion?
                                </label>
                                <textarea
                                  name="reason_suggestion"
                                  className="form-control-custom"
                                  placeholder="Tell us more about why you're leaving..."
                                  value={reasonSuggestion}
                                  onChange={(e) => setReasonSuggestion(e.target.value)}
                                />
                              </div>
            
                              <div className='password-section'>
                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '10px', display: 'block' }}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2m3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2"/>
                                  </svg>
                                  Confirm your password to delete
                                </label>
                                <input
                                  type="password"
                                  name="password"
                                  className="form-control-custom"
                                  placeholder="Enter your password"
                                  required
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                />
                              </div>
            
                              <div className='action-section'>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <Link 
                  to='/profile-settings'
                  style={{ 
                    flex: '1',
                    padding: '14px',
                    fontSize: '17px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    borderRadius: '14px',
                    border: '2px solid #dc3545',
                    background: 'white',
                    color: '#dc3545',
                    textDecoration: 'none',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Cancel
                </Link>
                <button
              className='btn-delete'
              onClick={handleDeleteAccount}
              style={{ flex: '1', width: 'auto' }}
            >
              Delete 
            </button>
              </div>
            </div>
            
                            </div>
                     

            <div className='row end-page-space'></div>
          </div>
        </div>

        <style>{`
        

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .delete-header-custom {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          padding: 50px 40px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .delete-header-custom::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: pulse 3s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        .delete-icon {
          width: 80px;
          height: 80px;
         
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          backdrop-filter: blur(10px);
        }

        .delete-icon svg {
          width: 45px;
          height: 45px;
          color: white;
        }

        .delete-header-custom h1 {
          color: white;
          font-size: 32px;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.5px;
          position: relative;
          z-index: 1;
        }

        .delete-header-custom p {
          color: rgba(255, 255, 255, 0.9);
          margin: 10px 0 0;
          font-size: 16px;
          position: relative;
          z-index: 1;
        }

        .delete-body-custom {
          padding: 45px;
        }

        .danger-alert {
          background: linear-gradient(135deg, #ffe6e6 0%, #ffcccc 100%);
          border-left: 5px solid #dc3545;
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 35px;
          box-shadow: 0 4px 15px rgba(220, 53, 69, 0.15);
        }

        .danger-alert-title {
          color: #c82333;
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .danger-alert-title svg {
          width: 24px;
          height: 24px;
        }

        .danger-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .danger-list li {
          color: #c82333;
          padding: 12px 0;
          padding-left: 35px;
          position: relative;
          font-size: 15px;
          font-weight: 500;
        }

        .danger-list li:before {
          content: "";
          position: absolute;
          left: 0;
          top: 14px;
          width: 20px;
          height: 20px;
      
          border-radius: 50%;
        }

        .danger-list li:after {
          content: "⚠";
          position: absolute;
          left: 4px;
          top: 10px;
          color: white;
          font-weight: bold;
          font-size: 14px;
        }

        .section-title {
          color: #184A2C;
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 25px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .section-title svg {
          width: 24px;
          height: 24px;
          color: #75B437;
        }

        .reason-checkbox {
          display: block;
          padding: 15px 20px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #fafafa;
        }

        .reason-checkbox:hover {
          border-color: #75B437;
          background: #f0f8e8;
          transform: translateX(5px);
        }

        .reason-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          margin-right: 12px;
          cursor: pointer;
          accent-color: #184A2C;
        }

        .reason-checkbox.selected {
          border-color: #184A2C;
          background: #f0f8e8;
          box-shadow: 0 4px 12px rgba(24, 74, 44, 0.15);
        }

        .reason-checkbox label {
          cursor: pointer;
          font-weight: 500;
          color: #333;
          display: flex;
          align-items: center;
          margin: 0;
        }

        .form-control-custom {
          width: 100%;
          padding: 15px 20px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.3s ease;
          background: #fafafa;
        }

        .form-control-custom:focus {
          outline: none;
          border-color: #75B437;
          background: white;
          box-shadow: 0 0 0 4px rgba(117, 180, 55, 0.1);
        }

        .form-control-custom::placeholder {
          color: #999;
        }

        textarea.form-control-custom {
          min-height: 120px;
          resize: vertical;
        }

        .password-section {
          margin-top: 30px;
          padding-top: 30px;
          border-top: 2px solid #f0f0f0;
        }
.action-section {
  margin-top: 35px;
  margin-bottom: 20px;
  padding-top: 30px;
  border-top: 2px solid #f0f0f0;
}
       
        .btn-delete {
  padding: 16px;
  font-size: 17px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.35);
  position: relative;
  overflow: hidden;
}

        .btn-delete::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .btn-delete:hover::before {
          width: 300px;
          height: 300px;
        }

        .btn-delete:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(220, 53, 69, 0.45);
        }

        .btn-delete:active {
          transform: translateY(-1px);
        }

        .btn-delete:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .loading-state {
          text-align: center;
          padding: 50px 20px;
        }

        .spinner-custom {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #dc3545;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text {
          color: #666;
          font-size: 16px;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          
        

          .delete-header-custom {
            padding: 40px 25px;
          }

          .delete-header-custom h1 {
            font-size: 26px;
          }

          .delete-body-custom {
            padding: 30px 20px;
          }

          .section-title {
            font-size: 18px;
          }
        }
      `}</style>


      </div>
    </div>
  );
}

export default DeleteAccount;
